import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { Button, Spin, Upload, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { uploadFile } from '../../api/utils/api-files'
import { API_BASE_URL } from '../../config'
import { useSmartTranslation } from '../../cls/TranslationContext'

const MAX_SIZE_MB = 5
const ALLOWED_FILE_TYPES = [
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]

export const UPLOAD_URL = API_BASE_URL + 'uploads/'
/**
 * FileUpload component provides a button and upload mechanism for attaching files to an entity.
 * It validates file type and size, displays upload progress, and manages the uploaded file list.
 *
 * @component
 * @param {Object} props
 * @param {string} props.entity_table – The backend table name to which files belong.
 * @param {string} props.entity_uid – The unique ID of the entity record.
 * @param {string[]} [props.allowedFileTypes=ALLOWED_FILE_TYPES] – MIME types allowed for upload.
 * @param {number} [props.allowedFileSize=MAX_SIZE_MB] – Max file size in MB.
 * @param {Function} props.onChange – Callback receiving an array of uploaded file UIDs.
 * @param {Object[]} [props.initialFileList=[]] – Pre-populated list of files ({ uid, name, status, url }).
 * @param {boolean} [props.disabled=false] – When true, disables upload and removal.
 * @param {boolean} [props.multiple=true] – Allow selecting multiple files.
 * @returns {JSX.Element}
 */
const FileUpload = ({
	entity_table = '',
	entity_uid = '',
	allowedFileTypes = ALLOWED_FILE_TYPES,
	allowedFileSize = MAX_SIZE_MB,
	onChange,
	initialFileList = [],
	disabled = false,
	multiple = true // Default to allowing multiple files
}) => {
	if (!entity_table || !entity_uid) {
		throw new Error('entity_table and entity_uid are required')
	}
	const { t } = useSmartTranslation()
	const MAX_SIZE = allowedFileSize * 1024 * 1024
	const [uploading, setUploading] = useState(false)
	const [fileList, setFileList] = useState(initialFileList)
	const [progress, setProgress] = useState(0)
	/**
	 * beforeUpload validates the file type and size, then uploads it via API.
	 * On success, it updates the fileList state and calls onChange with new UIDs.
	 *
	 * @async
	 * @private
	 * @param {File} file – The file selected by the user.
	 * @returns {Promise<boolean>} Always returns false to prevent AntD auto-upload.
	 */
	const uploadProps = {
		beforeUpload: async (file) => {
			const isFileTypeValid = allowedFileTypes.includes(file.type)
			const isFileSizeValid = file.size <= MAX_SIZE

			if (!isFileTypeValid) {
				message.error(
					`You can only upload files of type: ${allowedFileTypes.join(
						', '
					)}`
				)
				return false
			}

			if (!isFileSizeValid) {
				message.error(`File must be smaller than ${MAX_SIZE_MB}MB!`)
				return false
			}

			const formData = new FormData()
			const fileUuid = uuidv4()
			const fileExtension = file.name.split('.').pop()
			const newFileName = `${entity_table}_${entity_uid}_${fileUuid}.${fileExtension}`

			formData.append('file', file)
			formData.append('newFileName', newFileName)

			try {
				setUploading(true)
				setProgress(0)

				const response = await uploadFile(formData, {
					onUploadProgress: (event) => {
						if (event.lengthComputable) {
							const percent = Math.round(
								(event.loaded * 100) / event.total
							)
							setProgress(percent)
						}
					}
				})
				// console.log('Upload response:', response)
				if (response.data.status === 'success') {
					const uploadedFileName = response.data.fileName
					const newFileItem = {
						uid: newFileName,
						name: file.name,
						status: 'done',
						url: UPLOAD_URL + uploadedFileName
					}

					// Append or replace file in file list depending on `multiple`
					const updatedFileList = multiple
						? [...fileList, newFileItem] // Add to list for multiple
						: [newFileItem] // Replace for single file

					setFileList(updatedFileList)
					onChange(updatedFileList.map((file) => file.uid)) // Pass only names
				} else {
					console.error(`${file.name} upload failed.`)
				}
			} catch (error) {
				console.error(`Upload error: ${error}`)
			} finally {
				setUploading(false)
			}

			return false
		},
		onRemove: (file) => {
			if (disabled) return
			const newFileList = fileList.filter((item) => item.uid !== file.uid)
			setFileList(newFileList)
			onChange(newFileList.map((file) => file.uid))
		},
		fileList,
		multiple // Pass the `multiple` prop to the Upload component
	}

	return (
		<Upload
			className="we-file-upload"
			accept={allowedFileTypes.join(',')}
			{...uploadProps}
		>
			<Button
				type="default"
				icon={uploading ? <Spin size="small" /> : <UploadOutlined />}
				disabled={uploading || disabled}
			>
				{uploading
					? t('single.uploading', { percent: progress })
					: t('single.upload')}
			</Button>
		</Upload>
	)
}

FileUpload.propTypes = {
	entity_table: PropTypes.string,
	entity_uid: PropTypes.string,
	onChange: PropTypes.func,
	allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
	allowedFileSize: PropTypes.number,
	initialFileList: PropTypes.arrayOf(PropTypes.object),
	disabled: PropTypes.bool,
	multiple: PropTypes.bool // New prop type for `multiple`
}

export default FileUpload
